import { useEffect, useRef, useState, FC } from "react";

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { LayerToggle } from "types/LayerToggle";
import { MapStyle } from "types/MapStyle";
import { SpaceType } from "types/SpaceType";

import styles from "components/Map/Map.module.css";
import Toggle from "components/Toggle/Toggle";
import SearchBar from "components/SearchBar/SearchBar";

import useMapEvents from "hooks/useMapEvents";
import { useMapControls } from "hooks/useMapControls";
import usePersonalSpaces from "hooks/usePersonalSpaces";
import { useMapStyles } from "hooks/useMapStyles";

import { layersConfig } from "config/layersConfig";
import { defaultMapConfig, mapStyles } from "config/mapConfig";

if (!process.env.REACT_APP_MAPBOX_TOKEN) {
	throw new Error(
		"Mapbox token is required. Please check your environment variables."
	);
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

type Props = {
	initialCenter?: [number, number];
	initialZoom?: number;
};

const Map: FC<Props> = ({ initialCenter = [-74.5, 40], initialZoom = 9 }) => {
	const mapContainer = useRef<HTMLDivElement>(null);
	const map = useRef<mapboxgl.Map | null>(null);
	const [mapInitialized, setMapInitialized] = useState(false);

	const [layers, setLayers] = useState<LayerToggle[]>(layersConfig);

	const {
		showSpace,
		toggleSpaceActive,
		// removeMarkers, // TODO: Implement the removal of markers.
		handleBarrierMarkers,
		handleHealthcareMarkers,
		handleFoodMarkers,
		handleEducationMarkers,
		handleCommunityMarkers,
		handleTransportationMarkers,
		handleHousingMarkers,
		handleEconomicMarkers,
	} = useMapEvents({
		map,
	});

	// Handle map style changes
	const { changeMapStyle, currentStyle } = useMapStyles(map, mapInitialized);

	// Add map controls.
	useMapControls(map, mapInitialized);

	// Add personal space markers.
	const { handlePersonalSpaceMarkers } = usePersonalSpaces(map);

	// Initialize map.
	useEffect(() => {
		if (!mapContainer.current) return;

		map.current = new mapboxgl.Map({
			container: mapContainer.current,
			center: initialCenter,
			zoom: initialZoom,
			...defaultMapConfig,
			style: mapStyles[currentStyle] ?? defaultMapConfig.style,
		});

		const mapInstance = map.current;

		mapInstance.on("load", () => {
			setMapInitialized(true);
		});

		// Cleanup function
		return () => {
			if (mapInstance) {
				map.current?.remove();
				map.current = null;
			}
		};
	}, [initialCenter, initialZoom, currentStyle, map]);

	return (
		<div className={styles.mapContainer}>
			<div ref={mapContainer} className={styles.mapWrapper} />

			<div className={styles.controls}>
				{layers.map((layer) => (
					<Toggle
						key={layer.id}
						isActive={layer.isActive}
						label={layer.label}
						icon={layer.icon}
						onChange={(value: string) => {
							setLayers((prev) => {
								return prev.map((l) => {
									if (l.id === layer.id) {
										return { ...l, isActive: value === "true" };
									}
									return l;
								});
							});

							if (layer.id === "map-style") {
								changeMapStyle(value as MapStyle);
								return;
							}

							if (layer.id === "personal-spaces") {
								handlePersonalSpaceMarkers(value);
								return;
							}

							if (layer.id === "barriers") {
								toggleSpaceActive(value, "barriers");
								handleBarrierMarkers(value);
								return;
							}

							if (layer.id === "healthcare") {
								toggleSpaceActive(value, "healthcare");
								handleHealthcareMarkers(value);
								return;
							}

							if (layer.id === "food-nutrition") {
								toggleSpaceActive(value, "food-nutrition");
								handleFoodMarkers(value);
								return;
							}

							if (layer.id === "education") {
								toggleSpaceActive(value, "education");
								handleEducationMarkers(value);
								return;
							}

							if (layer.id === "community") {
								toggleSpaceActive(value, "community");
								handleCommunityMarkers(value);
								return;
							}

							if (layer.id === "transportation") {
								toggleSpaceActive(value, "transportation");
								handleTransportationMarkers(value);
								return;
							}

							if (layer.id === "housing") {
								toggleSpaceActive(value, "housing");
								handleHousingMarkers(value);
								return;
							}

							if (layer.id === "economic") {
								toggleSpaceActive(value, "economic");
								handleEconomicMarkers(value);
								return;
							}

							toggleSpaceActive(value, layer.id as SpaceType);
						}}
						spaces={showSpace(layer.id)}
						showOptions={layer.id === "map-style"}
						currentValue={layer.id === "map-style" ? currentStyle : undefined}
						options={[
							{ value: "streets", label: "Streets", icon: "🛣️" },
							{ value: "satellite", label: "Satellite", icon: "🛰️" },
							{ value: "light", label: "Light", icon: "☀️" },
							{ value: "dark", label: "Dark", icon: "🌙" },
							{ value: "outdoors", label: "Outdoors", icon: "🏞️" },
						]}
					/>
				))}
			</div>

			<div className={styles.searchBarContainer}>
				<SearchBar map={map} />
			</div>
		</div>
	);
};

export default Map;
